import dotPropImmutable from 'dot-prop-immutable';
import { keyBy } from 'lodash';
import moment from 'moment';
import { DateType } from 'api/models';
import { insert } from 'data/templates/projects/utils';
import { byStartDate } from 'utils/comparators/by-start-date';
import {
  DateType as _DateType,
  getDuration,
  getNewDates,
} from 'utils/date-shift';

const toTasksHashMap = (tasks) =>
  tasks.reduce(
    (resultTasks, task) => ({
      [task.id]: task,
      ...((task.children ?? []).length > 0
        ? toTasksHashMap(task.children ?? [])
        : {}),
      ...resultTasks,
    }),
    {},
  );

export const toHashMap = (data) =>
  (data ?? []).reduce(
    (result, projectPhase) => ({
      projectPhases: {
        ...result.projectPhases,
        [projectPhase.id]: projectPhase,
      },
      tasks: {
        ...result.tasks,
        ...toTasksHashMap(projectPhase.children ?? []),
      },
    }),
    { projectPhases: {}, tasks: {} },
  );

const getChildTasks = (
  tasks,
  tasksHashMap,
  level,
  isParentPrivate: boolean,
) => {
  const childtasks = tasks?.reduce((acc, id) => {
    const task = tasksHashMap[id];

    if (task === undefined) {
      return acc;
    }

    const newTask = {
      ...task,
      level,
      className: `level-${level} ${
        task?.private || isParentPrivate ? 'private' : ''
      }`,
      children: getChildTasks(
        task?.data?.subTasks,
        tasksHashMap,
        level + 1,
        isParentPrivate || task?.private,
      ),
    };

    return [...acc, newTask];
  }, []);

  return childtasks;
};

export const toTreeData = ({ projectPhases, tasks }) => {
  if (tasks && projectPhases) {
    return Object.keys(projectPhases)
      .map((projectPhaseId) => {
        const projectPhase = projectPhases[projectPhaseId];

        return {
          ...projectPhase,
          level: 0,
          className: `project-phase ${projectPhase.private ? 'private' : ''}`,
          children: getChildTasks(
            projectPhase?.data?.taskOrder,
            tasks,
            1,
            projectPhase.private,
          ),
        };
      })
      .filter(Boolean);
  }

  return [];
};

export const removeTaskFromPhase = (
  data,
  projectPhaseId: number,
  taskId: number,
) => {
  const tasksPath = `projectPhases.${projectPhaseId}.data.taskOrder`;
  const phaseTasks = dotPropImmutable.get(data, tasksPath, []);

  return dotPropImmutable.set(
    data,
    tasksPath,
    phaseTasks.filter((id) => id !== taskId),
  );
};

export const removeSubTaskFromTask = (
  data,
  parentTaskId: number,
  taskId: number,
) => {
  const tasksPath = `tasks.${parentTaskId}.data.subTasks`;
  const taskSubTasks = dotPropImmutable.get(data, tasksPath, []);

  return dotPropImmutable.set(
    data,
    tasksPath,
    taskSubTasks.filter((id) => id !== taskId),
  );
};

export const removeTaskFromParent = (data, task) => {
  if (
    (task.data.projectPhase && !task.data.parentTask) ||
    (!task.data.parentTaskObject.taskId && !task.data.parentTask)
  ) {
    const _data = removeTaskFromPhase(
      data,
      task.data.projectPhase.projectPhaseId,
      task.id,
    );

    return _data;
  }

  return removeSubTaskFromTask(
    data,
    task.data.parentTask?.taskId || task.data.parentTaskObject?.taskId,
    task.id,
  );
};

export const insertTaskIntoPhase = (
  data,
  projectPhaseId: number,
  projectPhaseName: string,
  taskId: number,
  insertAt: number,
) => {
  const path = `projectPhases.${projectPhaseId}.data.taskOrder`;
  const tasks = dotPropImmutable.get(data, path, []);

  let updated = dotPropImmutable.set(
    data,
    path,
    insert(tasks, insertAt, taskId),
  );

  updated = dotPropImmutable.set(updated, `tasks.${taskId}.data.projectPhase`, {
    projectPhaseId,
    projectPhaseName,
  });

  updated = dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.parentTask`,
    undefined,
  );

  return dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.parentTaskObject`,
    undefined,
  );
};

export const insertSubTaskIntoTask = (
  data,
  parentTaskId: number,
  parentTaskName: string,
  taskId: number,
  insertAt: number,
) => {
  const path = `tasks.${parentTaskId}.data.subTasks`;

  const subTasks = dotPropImmutable.get(data, path, []);

  let updated = dotPropImmutable.set(
    data,
    path,
    insert(subTasks, insertAt, taskId),
  );

  updated = dotPropImmutable.set(updated, `tasks.${taskId}.data.projectPhase`, {
    projectPhaseId: data.tasks[parentTaskId].data.projectPhase.projectPhaseId,
    projectPhaseName:
      data.tasks[parentTaskId].data.projectPhase.projectPhaseName,
  });

  updated = dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.parentTaskObject`,
    {
      taskId: parentTaskId,
      taskName: parentTaskName,
    },
  );

  return dotPropImmutable.set(
    updated,
    `tasks.${taskId}.data.parentTask`,
    parentTaskId,
  );
};

export const insertTaskIntoParent = (
  data,
  newParent,
  taskId: number,
  insertAt: number,
) => {
  if (newParent.type === 'PROJECT_PHASE') {
    return {
      data: insertTaskIntoPhase(
        data,
        newParent.id,
        newParent.title,
        taskId,
        insertAt,
      ),
      parent: { projectPhase: { projectPhaseId: newParent.id } },
    };
  }

  return {
    data: insertSubTaskIntoTask(
      data,
      newParent.id,
      newParent.title,
      taskId,
      insertAt,
    ),
    parent: { parentTask: { taskId: newParent.id } },
  };
};

export const getParent = (data, task) => {
  if (task.data.parentTaskObject && task.data.parentTaskObject.taskId) {
    return dotPropImmutable.get(data, `tasks.${task.data.parentTask}`, {});
  }

  return dotPropImmutable.get(
    data,
    `projectPhases.${task.data.projectPhase.projectPhaseId}`,
    {},
  );
};

export const getSibilings = (data, task): number[] => {
  if (task.data.parentTaskObject && task.data.parentTaskObject.taskId) {
    return dotPropImmutable.get(
      data,
      `tasks.${task.data.parentTask}.data.subTasks`,
      [],
    );
  }

  return dotPropImmutable.get(
    data,
    `projectPhases.${task.data.projectPhase.projectPhaseId}.data.taskOrder`,
    [],
  );
};

export const getDepthOfTask = (data, taskId: number): number => {
  const subTasks: number[] = dotPropImmutable.get(
    data,
    `tasks.${taskId}.data.subTasks`,
    [],
  );

  if (subTasks.length === 0) {
    return 1;
  }

  return (
    1 +
    subTasks.reduce((maxLevelDeep, subTaskId) => {
      const levelDeep = getDepthOfTask(data, subTaskId);

      return levelDeep > maxLevelDeep ? levelDeep : maxLevelDeep;
    }, 0)
  );
};

export const isTaskDatesOutOfParentDateRange = (
  taskDates: [DateType, DateType],
  parentDates: [DateType, DateType],
): boolean => {
  const [taskStartDate, taskDueDate] = taskDates;
  const [parentStartDate, parentDueDate] = parentDates;
  const pStartDate = moment(parentStartDate);
  const pDueDate = moment(parentDueDate);

  let result = false;

  if (taskStartDate) {
    const tStartDate = moment(taskStartDate);

    result =
      tStartDate.isBefore(pStartDate, 'date') ||
      tStartDate.isAfter(pDueDate, 'date');
  }

  if (taskDueDate && !result) {
    const tDueDate = moment(taskDueDate);

    result =
      tDueDate.isAfter(pDueDate, 'date') ||
      tDueDate.isBefore(pStartDate, 'date');
  }

  return result;
};

export const getTasksPayload = (data, id, parentTaskDates, isDraggedTask) => {
  const duration = getDuration(
    data.tasks[id].data.startDate ?? data.tasks[id].data.dueDate,
    data.tasks[id].data.dueDate ?? data.tasks[id].data.startDate,
  );

  let startDate;
  let dueDate;

  if (isDraggedTask) {
    [startDate, dueDate] = parentTaskDates[0]
      ? getNewDates(parentTaskDates[0], duration, _DateType.StartDate)
      : parentTaskDates[1]
      ? getNewDates(parentTaskDates[1], duration, _DateType.DueDate)
      : [data.tasks[id].data.startDate, data.tasks[id].data.dueDate];
  } else {
    [startDate, dueDate] = [
      data.tasks[id].data.startDate,
      data.tasks[id].data.dueDate,
    ];
  }

  return {
    startDate,
    dueDate,
    taskName: data.tasks[id].data.taskName,
    taskId: data.tasks[id].data.taskId,
    projectPhase: data.tasks[id].data.projectPhase,
    parentTaskObject: data.tasks[id].data.parentTaskObject ?? null,
    parentTask: data.tasks[id].data.parentTask ?? null,
    private: data.tasks[id].data.private,
    subTaskOrder: data.tasks[id].data.subTasks ?? null,
  };
};

export const convertToTaskChildObject = (
  key,
  name,
  startDate,
  dueDate,
  phaseId,
  phaseName,
  status,
  parentTaskDates,
) => {
  const _startDate = startDate ?? dueDate;
  const _dueDate = dueDate ?? startDate;

  return {
    key,
    name,
    startDate,
    currentDates: [_startDate, _dueDate],
    newDates: [],
    duration: getDuration(_startDate, _dueDate),
    children: [],
    projectPhase: {
      projectPhaseName: phaseName,
      projectPhaseId: phaseId,
    },
    status,
    type: 'TASK',
    parentTaskDates,
  };
};

export const convertToPhaseObject = (key, name, startDate, dueDate) => {
  return {
    key,
    name,
    startDate,
    currentDates: [startDate, dueDate],
    newDates: [],
    duration: getDuration(startDate, dueDate),
    children: [],
    numberOfTasks: 1,
    type: 'PROJECT_PHASE',
  };
};

export const handleDateConflictsArray = (
  conflicts,
  droppedPhaseId,
  draggedTask,
  dataAsHashMap,
  parentTaskDates,
) => {
  let arr;

  if (conflicts.find((item) => item.key === droppedPhaseId)) {
    arr = conflicts.map((item) => {
      if (item.key === droppedPhaseId) {
        return {
          ...item,
          children: [
            ...item.children.filter((task) => task.key !== draggedTask.id),
            convertToTaskChildObject(
              draggedTask.id,
              draggedTask.title,
              draggedTask.startDate,
              draggedTask.dueDate,
              droppedPhaseId,
              dataAsHashMap.projectPhases[droppedPhaseId].title,
              draggedTask.data.fields.find(
                (field) => field.fieldName === 'Status',
              ),
              parentTaskDates,
            ),
          ],
          numberOfTasks: item?.children?.length,
        };
      }

      return item;
    });
  } else {
    const phaseobject = convertToPhaseObject(
      droppedPhaseId,
      dataAsHashMap.projectPhases[droppedPhaseId].title,
      dataAsHashMap.projectPhases[droppedPhaseId].startDate,
      dataAsHashMap.projectPhases[droppedPhaseId].dueDate,
    );

    const taskObject = convertToTaskChildObject(
      draggedTask.id,
      draggedTask.title,
      draggedTask.startDate,
      draggedTask.dueDate,
      droppedPhaseId,
      dataAsHashMap.projectPhases[droppedPhaseId].title,
      draggedTask.data.fields.find((field) => field.fieldName === 'Status'),
      parentTaskDates,
    );

    arr = [
      ...conflicts,
      {
        ...phaseobject,
        children: [taskObject],
      },
    ];
  }

  return arr;
};

export const getUpdatedActions = (actions, dataSource) => {
  // Create copies of updatedPhases and updatedTasks arrays
  const updatedPhases = actions.updatedPhases.map((phase) => ({ ...phase }));
  const updatedTasks = actions.updatedTasks.map((task) => ({ ...task }));

  const phasesMap = keyBy(updatedPhases, 'projectPhaseId');
  const tasksMap = keyBy(updatedTasks, 'taskId');

  // Update phases and tasks in the copied arrays
  dataSource.forEach((sourceItem) => {
    const phaseToUpdate = phasesMap[sourceItem.key];

    if (phaseToUpdate) {
      const [startDate, dueDate] = sourceItem.newDates;

      phaseToUpdate.startDate = startDate;
      phaseToUpdate.dueDate = dueDate;
    }

    if (sourceItem.children) {
      sourceItem.children.forEach((child) => {
        const taskToUpdate = tasksMap[child.key];

        if (taskToUpdate) {
          const [startDate, dueDate] = child.newDates;

          // taskToUpdate.dates = child.newDates;
          taskToUpdate.startDate = startDate;
          taskToUpdate.dueDate = dueDate;
        }
      });
    }
  });

  // Return a new object with updatedPhases and updatedTasks arrays
  return {
    updatedPhases,
    updatedTasks,
  };
};

export const sortDataSource = (dataSource) => {
  return dataSource
    .map((phase) => {
      const results = phase?.children?.sort(byStartDate);

      return {
        ...phase,
        children: results,
      };
    })
    .sort(byStartDate);
};
